import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { InfoHint } from '../common/InfoHint';
import { JsonSchemaBuilder } from '../common/JsonSchemaBuilder';
import { JsonataMappingBuilder } from '../common/JsonataMappingBuilder';

import {
  fromMappingRows,
  MappingRow,
  mappingSmallBtn,
  toMappingRows,
  topLevelKeys,
  type IConnectSource,
  type IMappingRow,
} from './MappingRow';
import { useWorkflowEditor } from './WorkflowEditorContext';
import { buildIncomingWiringBadges, IncomingWiringBadges } from './edge-wiring-chip';
import { getEffectiveOutputFieldNamesByNodeKey } from './node-configs/effective-output-schema';
import { normalizeRegistryOutputSchema } from './node-configs/node-type-schemas';

/**
 * Per-node schema editor — surfaces the three fields that wire a node
 * into the graph and constrain its I/O:
 *
 *  1. `inputMapping` — `Record<string, unknown>`. For each key, either a
 *     JSONata expression (string starting with `$`, evaluated against the
 *     context `{ __input, <upstreamNodeKey>: <output>, … }`) or a literal
 *     value passed through as-is. This is THE mechanism for wiring an
 *     upstream node's output into this node's input. See
 *     apps/runner/src/execution/input-mapper.ts:18-46.
 *
 *  2. `outputSchema` — JSON Schema validated against the node's return
 *     value at runtime (in addition to the node-type's own schema).
 *
 *  3. `outputMapping` — optional JSONata expression evaluated on the
 *     node's raw return (with full `previousOutputs` in scope) to compose
 *     the recorded node output. (DEV-15895 / EU-28.)
 */

const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};

const cardStyle: CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-1, var(--surface-2))',
};

// Local style aliases for the few inline buttons/textareas inside this
// editor; the row's own styles live in MappingRow.tsx alongside the
// shared MappingRow component.
const monoInput: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 12,
  padding: '4px 6px',
  height: 26,
};

const smallBtn = mappingSmallBtn;

export function NodeSchemaEditor({ nodeKey }: { nodeKey: string }) {
  const editor = useWorkflowEditor();
  const node = editor.getNode(nodeKey);
  const nodeType = String((node?.data as { nodeType?: string } | undefined)?.nodeType ?? '');
  const isLlmAgent = nodeType === 'llm-agent';
  // llm-agent nodes are self-contained (DEV-16943): the output schema and
  // instructions live INLINE in the node config, not on a referenced Agent.
  // Read them straight off the node config so the preview mirrors what the
  // node-config panel edits — no agent fetch.
  const llmConfig = (
    node?.data as { config?: { outputSchema?: unknown; instructions?: unknown } } | undefined
  )?.config;
  const llmOutputSchema =
    llmConfig?.outputSchema &&
    typeof llmConfig.outputSchema === 'object' &&
    !Array.isArray(llmConfig.outputSchema)
      ? (llmConfig.outputSchema as Record<string, unknown>)
      : null;
  const llmInstructions = typeof llmConfig?.instructions === 'string' ? llmConfig.instructions : '';

  // Upstream-reference picker: every other node + the run trigger input.
  // We don't constrain to topo-upstream because save-time validation in
  // the runner handles cycles; offering all keys keeps authoring fluid.
  const refOptions = useMemo(() => {
    const ids = editor.nodes.map((n) => n.id).filter((id) => id !== nodeKey);
    return ['__input', ...ids];
  }, [editor.nodes, nodeKey]);

  const data = (node?.data ?? {}) as {
    inputMapping?: Record<string, unknown>;
    outputSchema?: Record<string, unknown>;
    outputMapping?: string | null;
  };
  const inputMapping = data.inputMapping ?? {};
  const outputSchema = data.outputSchema ?? {};
  const outputMapping = data.outputMapping ?? '';

  // Local row state, so freshly-added rows with empty keys can exist
  // during authoring. The persisted inputMapping (Record<string,
  // unknown>) can't represent an empty key, so `fromMappingRows` drops them on
  // the way out — without local state the row would vanish the instant
  // it was added. Re-sync whenever the underlying node changes (e.g.
  // version reload or switching to a different node). Hooks must run on
  // every render, so this lives before the not-found early return.
  const inputMappingSig = JSON.stringify(inputMapping);
  const [rows, setRows] = useState<IMappingRow[]>(() => toMappingRows(inputMapping));
  useEffect(() => {
    setRows(toMappingRows(inputMapping));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey, inputMappingSig]);

  if (!node) {
    return (
      <div className="alert alert--warn" style={{ fontSize: 12 }}>
        Node <code>{nodeKey}</code> not found in the current graph.
      </div>
    );
  }

  const updateRows = (next: IMappingRow[]) => {
    setRows(next);
    editor.updateNode(nodeKey, { inputMapping: fromMappingRows(next) });
  };

  const setMappingFromRaw = (next: Record<string, unknown>) => {
    setRows(toMappingRows(next));
    editor.updateNode(nodeKey, { inputMapping: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* ─── Inputs ───────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <label className="t-secondary">
          Inputs
          <InfoHint
            text={
              <>
                Builds this node&apos;s input before it runs. Leave empty to pass the workflow
                trigger input through as-is — that&apos;s the common case for the first node after
                the trigger. Add rows to pull fields from the trigger or any upstream node. Switch a
                row to <em>Advanced</em> for expressions or literals.
              </>
            }
          />
        </label>
        <IncomingWiringSummary nodeKey={nodeKey} />
        <InputMappingEditor
          rows={rows}
          mapping={inputMapping}
          refOptions={refOptions}
          onChangeRows={updateRows}
          onChangeRaw={setMappingFromRaw}
        />
      </div>

      {/* ─── Output renamings ───────────────────────────────────────
          Hidden for `llm-agent` nodes: the node's inline structured-output
          schema (config.outputSchema) is the contract, so post-hoc JSONata
          transforms aren't part of the authoring model for these nodes.
      */}
      {!isLlmAgent && (
        <div style={sectionStyle}>
          <label className="t-secondary">
            Output renamings
            <InfoHint
              text={
                <>
                  Optional JSONata that transforms this node&apos;s raw return value before
                  it&apos;s stored. Has access to all prior outputs via{' '}
                  <code>$.&lt;nodeKey&gt;</code> and the raw return as <code>$.__raw</code>. Clear
                  to pass the raw value through unchanged.
                </>
              }
            />
          </label>
          <JsonataMappingBuilder
            value={outputMapping}
            onChange={(next) =>
              editor.updateNode(nodeKey, { outputMapping: next.trim() === '' ? null : next })
            }
            availableNodeKeys={refOptions}
          />
        </div>
      )}

      <div style={sectionStyle}>
        <label className="t-secondary">
          Output validation
          <InfoHint
            text={
              isLlmAgent ? (
                <>
                  Defined inline in this node&apos;s config. Edit the <strong>Output schema</strong>{' '}
                  in the node config panel; it&apos;s shown here read-only so the Connect-mode field
                  list stays in sync.
                </>
              ) : (
                <>
                  JSON Schema validating this node&apos;s return value at runtime. New nodes start
                  with the <code>{nodeType}</code> type&apos;s default — edit freely to narrow or
                  extend. A mismatch fails the run with an <code>output-validation</code> error.
                </>
              )
            }
          />
        </label>
        {isLlmAgent ? (
          <LlmAgentPreviewPanel schema={llmOutputSchema} instructions={llmInstructions} />
        ) : (
          <JsonSchemaBuilder
            value={Object.keys(outputSchema).length === 0 ? null : outputSchema}
            onChange={(next) => editor.updateNode(nodeKey, { outputSchema: next ?? {} })}
          />
        )}
      </div>

      {/* ─── Save ─────────────────────────────────────────────────────
          Node-schema edits land in the same place as graph edits: the
          full-graph PATCH that the main top-bar Save also triggers. We
          expose a local Save button here so authors don't have to know
          the difference between "schema-only" saves (the buttons on the
          workflow input/output editors, which only PATCH those fields
          and let the server copy the graph forward verbatim) and
          full-graph saves. Without this, Node Schema edits silently
          stay dirty until the user hunts down the top-bar Save —
          observed empirically when authoring `inputMapping` only ever
          produced `"inputMapping": {}` in saved versions.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => editor.save.mutate()}
            disabled={editor.save.isPending || !editor.dirty || editor.isReadOnly}
            title={
              editor.isReadOnly
                ? 'Read-only — viewing a historical version. Promote it to edit.'
                : editor.dirty
                  ? 'Save the full workflow graph (cuts a new version).'
                  : 'No unsaved changes.'
            }
          >
            {editor.save.isPending ? 'Saving…' : 'Save (cut new version)'}
          </button>
          {editor.save.error ? (
            <span className="t-xs" style={{ color: 'var(--status-danger, var(--text-danger))' }}>
              {(editor.save.error as Error).message}
            </span>
          ) : null}
        </div>
        {editor.save.isSuccess && !editor.save.isPending && !editor.dirty ? (
          <span
            className="t-secondary"
            style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}
          >
            Saved — a new workflow version was cut.
          </span>
        ) : null}
        <p className="t-xs" style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Saves the entire graph (nodes + edges + per-node schemas), not just this node.
        </p>
      </div>
    </div>
  );
}

// ─── LLM-agent preview panel ─────────────────────────────────────────────

/** `<summary>` style — small, secondary, matches `<details>` widgets
 *  elsewhere in the workflow panels (e.g. LlmAgentConfig). */
const previewSummaryStyle: CSSProperties = {
  cursor: 'pointer',
  userSelect: 'none',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

/** `<pre>` style for the instructions text. Bounded height so a long
 *  system prompt doesn't push the Save button off-screen. */
const instructionsPreStyle: CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface-2)',
  padding: 'var(--sp-2)',
  margin: 0,
  marginTop: 'var(--sp-1)',
  fontSize: 'var(--fs-xs)',
  fontFamily: 'var(--font-mono)',
  whiteSpace: 'pre-wrap',
  maxHeight: 320,
  overflow: 'auto',
};

/**
 * Read-only preview for llm-agent nodes — both the structured output
 * schema and the agent's system prompt instructions. Output schema is
 * collapsible (default open) to make room for the instructions preview
 * (default closed) without monopolizing the panel.
 *
 * Read-only mirror of the node's INLINE config (DEV-16943): the output schema
 * and instructions live on the node itself and are edited in the node-config
 * panel. Shown here so the Connect-mode field list stays coherent without a
 * second editor for the same fields.
 */
function LlmAgentPreviewPanel({
  schema,
  instructions,
}: {
  schema: Record<string, unknown> | null;
  instructions: string | null;
}) {
  const normalized = normalizeRegistryOutputSchema(schema);
  const hasFields =
    normalized &&
    typeof normalized.properties === 'object' &&
    normalized.properties !== null &&
    Object.keys(normalized.properties as Record<string, unknown>).length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <details open={true}>
        <summary style={previewSummaryStyle}>Output schema</summary>
        <div style={{ marginTop: 'var(--sp-1)' }}>
          {hasFields ? (
            <JsonSchemaBuilder value={normalized} onChange={() => {}} readOnly={true} />
          ) : (
            <div
              className="t-xs"
              style={{ color: 'var(--text-secondary)', padding: 'var(--sp-2)' }}
            >
              — No output schema (text mode) —
            </div>
          )}
        </div>
      </details>
      <details>
        <summary style={previewSummaryStyle}>Instructions</summary>
        {instructions && instructions.trim().length > 0 ? (
          <pre style={instructionsPreStyle}>{instructions}</pre>
        ) : (
          <div className="t-xs" style={{ color: 'var(--text-secondary)', padding: 'var(--sp-2)' }}>
            — No instructions set —
          </div>
        )}
      </details>
      <div className="t-xs" style={{ color: 'var(--text-secondary)' }}>
        Defined inline in the node config — edit in the node&apos;s config panel.
      </div>
    </div>
  );
}

// ─── Input-mapping rows ───────────────────────────────────────────────────

function IncomingWiringSummary({ nodeKey }: { nodeKey: string }) {
  const editor = useWorkflowEditor();
  const badges = useMemo(
    () => buildIncomingWiringBadges(nodeKey, editor.edges),
    [nodeKey, editor.edges],
  );
  if (badges.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-2)',
      }}
    >
      <span className="t-xs t-secondary">Incoming connections</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {badges.map((b) => (
          <div key={b.edgeId} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IncomingWiringBadges badges={[b]} layout="wrap" />
              <code className="t-mono t-xs">{b.sourceKey}</code>
              {b.tone === 'success' ? <span className="t-xs t-secondary">{b.status}</span> : null}
            </div>
            {b.detail ? (
              <div
                className={b.tone === 'danger' ? 'alert alert--danger' : 'alert alert--warn'}
                style={{ fontSize: 12, margin: 0 }}
              >
                <strong>
                  {b.sourceKey}: {b.status}
                </strong>
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{b.detail}</div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Builder/Raw mode toggle wrapping the row editor. Mirrors the same
 * affordance on `JsonataMappingBuilder` (output mapping) so the two
 * mapping editors feel consistent.
 *
 *  - Builder mode: row-per-key UI (`InputMappingRows`).
 *  - Raw mode: JSON textarea showing the persisted
 *    `Record<string, unknown>`. Apply parses it back to the wire shape.
 */
function InputMappingEditor({
  rows,
  mapping,
  refOptions,
  onChangeRows,
  onChangeRaw,
}: {
  rows: IMappingRow[];
  mapping: Record<string, unknown>;
  refOptions: string[];
  onChangeRows: (next: IMappingRow[]) => void;
  onChangeRaw: (next: Record<string, unknown>) => void;
}) {
  const [mode, setMode] = useState<'builder' | 'raw'>('builder');
  const [rawText, setRawText] = useState(() => JSON.stringify(mapping, null, 2));
  const [rawError, setRawError] = useState<string | null>(null);

  // Re-sync the raw textarea whenever the persisted mapping changes from
  // outside (different node selected, version refetch, etc).
  const mappingSig = JSON.stringify(mapping);
  useEffect(() => {
    setRawText(JSON.stringify(mapping, null, 2));
    setRawError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingSig]);

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px var(--sp-2)',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'var(--surface-2)',
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <ModeToggle
            label="Builder"
            active={mode === 'builder'}
            onClick={() => setMode('builder')}
          />
          <ModeToggle label="Raw JSON" active={mode === 'raw'} onClick={() => setMode('raw')} />
        </div>
        <button
          type="button"
          style={{ ...smallBtn, borderColor: 'transparent' }}
          onClick={() => onChangeRaw({})}
          title="Clear all mappings"
        >
          Clear
        </button>
      </div>

      {mode === 'builder' ? (
        <InputMappingRows rows={rows} refOptions={refOptions} onChange={onChangeRows} />
      ) : (
        <div style={{ padding: 'var(--sp-2)' }}>
          <textarea
            className="input"
            rows={6}
            style={{ ...monoInput, height: 'auto', width: '100%' }}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            spellCheck={false}
          />
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              alignItems: 'center',
              marginTop: 'var(--sp-2)',
            }}
          >
            <button
              type="button"
              style={smallBtn}
              onClick={() => {
                const trimmed = rawText.trim();
                if (trimmed === '') {
                  onChangeRaw({});
                  setRawError(null);
                  setMode('builder');
                  return;
                }
                try {
                  const parsed: unknown = JSON.parse(trimmed);
                  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
                    setRawError('Input mapping must be a JSON object.');
                    return;
                  }
                  onChangeRaw(parsed as Record<string, unknown>);
                  setRawError(null);
                  setMode('builder');
                } catch (err) {
                  setRawError(`Invalid JSON: ${(err as Error).message}`);
                }
              }}
            >
              Apply
            </button>
            {rawError ? (
              <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                {rawError}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--surface)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
        border: '1px solid',
        borderColor: active ? 'var(--border-subtle)' : 'transparent',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 11,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function InputMappingRows({
  rows,
  refOptions,
  onChange,
}: {
  rows: IMappingRow[];
  // Kept for API stability with the parent component — Connect mode
  // builds its own richer source list, but the order in `refOptions`
  // (which already excludes the current node) is the canonical order we
  // want in the dropdown.
  refOptions: string[];
  onChange: (next: IMappingRow[]) => void;
}) {
  const editor = useWorkflowEditor();
  const schemaCtx = {
    nodeTypeOutputSchemaByKey: editor.nodeTypeOutputSchemaByKey,
  };

  // Build the Connect-mode source list from refOptions. Each option in
  // refOptions is a node key (or "__input"); we enrich it with the
  // human label + declared top-level output fields so the dropdowns can
  // render in user-friendly terms.
  const sources = useMemo<IConnectSource[]>(() => {
    return refOptions.map((k) => {
      if (k === '__input') {
        return {
          sourceKey: '__input',
          label: 'Workflow Input',
          fields: topLevelKeys(editor.workflowInputSchema),
        };
      }
      const upstream = editor.getNode(k);
      const data = (upstream?.data ?? {}) as {
        label?: unknown;
        nodeType?: string;
        outputSchema?: Record<string, unknown>;
        outputMapping?: string | null;
        config?: { agentId?: unknown };
      };
      const label = typeof data.label === 'string' && data.label.length > 0 ? data.label : k;
      return {
        sourceKey: k,
        label,
        fields: getEffectiveOutputFieldNamesByNodeKey(editor.getNode, k, schemaCtx),
      };
    });
  }, [refOptions, editor, schemaCtx]);

  const update = (i: number, patch: Partial<IMappingRow>) => {
    const next = rows.slice();
    const cur = next[i];
    if (!cur) return;
    next[i] = { ...cur, ...patch };
    onChange(next);
  };
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { key: '', value: '' }]);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-3)' }}>
        <div
          className="t-xs"
          style={{ color: 'var(--text-secondary)', marginBottom: 'var(--sp-2)' }}
        >
          No mappings — this node will receive the run trigger input verbatim.
        </div>
        <button type="button" style={smallBtn} onClick={add}>
          + Add input key
        </button>
      </div>
    );
  }

  return (
    <div>
      {rows.map((r, i) => (
        <MappingRow
          key={i}
          row={r}
          sources={sources}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
          keyPlaceholder="input key"
        />
      ))}
      <div style={{ padding: 'var(--sp-2)' }}>
        <button type="button" style={smallBtn} onClick={add}>
          + Add input key
        </button>
      </div>
    </div>
  );
}
